import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("padel_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("padel_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (emailOrPhone, password, padelCenterCode) => {
    const { data } = await api.post("/auth/login", {
      email_or_phone: emailOrPhone,
      password,
      padel_center_code: padelCenterCode || undefined,
    });
    localStorage.setItem("padel_token", data.token);
    // Fetch the full user (including language/permissions/etc) so context
    // consumers (LanguageSync, AuthContext.permissions) receive every field.
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      return me.data;
    } catch {
      setUser(data);
      return data;
    }
  };

  const register = async (formData) => {
    const { data } = await api.post("/auth/register", formData);
    localStorage.setItem("padel_token", data.token);
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      return me.data;
    } catch {
      setUser(data);
      return data;
    }
  };

  // Used by external auth providers (WhatsApp OTP) that already returned {token,user}
  const loginWithSession = async ({ token, user: userData }) => {
    if (!token) throw new Error("Missing token");
    localStorage.setItem("padel_token", token);
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      return me.data;
    } catch {
      setUser(userData);
      return userData;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout API error (continuing):", err);
    }
    localStorage.removeItem("padel_token");
    setUser(null);
  };

  // Effective per-resource creation permissions: admin & directivo always allowed.
  // jugador & profesor require explicit `permissions.can_create_<what>` flag set by admin.
  const permissions = useMemo(() => {
    const role = user?.role;
    const raw = user?.permissions || {};
    const baseline = role === "admin" || role === "directivo";
    return {
      canCreateRetas: baseline || !!raw.can_create_retas,
      canCreateTournaments: baseline || !!raw.can_create_tournaments,
      canCreateLeagues: baseline || !!raw.can_create_leagues,
      raw,
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithSession, logout, checkAuth, permissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be inside AuthProvider");
  return context;
}
